import {
    generateInvestorPayoutStatement,
    getMonthlyInvestorShare,
    getMonthlyInvestorShareById,
    getMonthlyInvestorShareIds,
    getMonthlyInvestorShareStats,
} from '@/api/monthly-investor-shares.api';
import {
    deleteInvestorYearlyPayout,
    fetchInvestorInvestorAnnualSettlements,
    fetchInvestorYearlyPayouts,
    findInvestorInfos,
    generateInvestorYearlyPayout,
    getInvestorInfo,
    getInvestorPortalAccess,
    getInvestorYearlyPayout,
    sendEmailInvestorYearlyPayout,
    setInvestorPortalAccess,
    updateInvestorYearlyPayout,
    updateMonthlyInvestorShare,
} from '@/api/property-management.api';
import { withLoading } from '@/shared';
import type { IBaseInvestorYearlyPayout, IInvestorInfoFetchOpts, IMonthlyInvestorShareFilterOptions, InvestorInfo } from '@condo/domain';
import type {
    FindInvestorInfosQueryParams,
    FindInvestorShareIdsQueryParams,
    FindInvestorYearlyPayoutsQueryParams,
    GenerateInvestorPayoutStatementMutationRequest,
    GenerateInvestorYearlyPayoutMutationRequest,
    GetInvestorYearlyPayoutQueryParams,
    GetMonthlyInvestorShareByIdQueryParams,
    GetMonthlyInvestorShareStatsQueryParams,
    InvestorAnnualSettlement,
    UpdateInvestorYearlyPayoutMutationRequest,
    UpdateMonthlyInvestorShareMutationRequest,
} from '@condo/sdk/propx';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import type { AxiosError } from 'axios';
import { type MaybeRef, computed, unref } from 'vue';

export const useInvestorInfo = (investorInfoId: MaybeRef<number | undefined>, opts?: IInvestorInfoFetchOpts) => {
    const enabled = computed(() => !!unref(investorInfoId));
    return useQuery(
        ['investor-info', investorInfoId, opts],
        async (): Promise<InvestorInfo | null> => withLoading(async () => getInvestorInfo(unref(investorInfoId)!, opts)),
        {
            enabled,
            initialData: null,
        },
    );
};

export const useInvestorInfos = (
    estateIdOrRef?: MaybeRef<number>,
    optsOrRef?: MaybeRef<FindInvestorInfosQueryParams['opts']>,
    enabledOrRef?: MaybeRef<boolean>,
) => {
    const estateId = computed(() => unref(estateIdOrRef));
    const opts = computed(() => unref(optsOrRef));
    const enabled = computed(() => unref(enabledOrRef));

    return useQuery(
        ['investor-infos', estateId, opts],
        async (): Promise<InvestorInfo[]> => {
            if (!estateId.value) {
                return [];
            }
            return withLoading(async () => findInvestorInfos({ estateId: estateId.value }, opts.value));
        },
        {
            initialData: [],
            enabled,
        },
    );
};

export const useInvestorPortalAccess = (investorInfoIdOrRef: MaybeRef<number | undefined>) => {
    const investorInfoId = computed(() => unref(investorInfoIdOrRef));

    return useQuery(
        ['investor-portal-access', investorInfoId],
        async () => {
            if (!investorInfoId.value) {
                return { portalAccessDisabled: false, canToggle: false };
            }
            return getInvestorPortalAccess(investorInfoId.value);
        },
        {
            enabled: computed(() => !!investorInfoId.value),
            initialData: { portalAccessDisabled: false, canToggle: false },
        },
    );
};

export const useSetInvestorPortalAccess = () => {
    const queryClient = useQueryClient();

    return useMutation(
        async ({ investorInfoId, portalAccessDisabled }: { investorInfoId: number; portalAccessDisabled: boolean }) =>
            setInvestorPortalAccess(investorInfoId, portalAccessDisabled),
        {
            onSuccess: (_data, variables) => {
                queryClient.invalidateQueries({ queryKey: ['investor-portal-access', variables.investorInfoId] });
            },
        },
    );
};

export const useInvestorAnnualSettlements = (params: MaybeRef<FindInvestorYearlyPayoutsQueryParams>) => {
    return useQuery(['investor-info-sev-annual-statement', 'estate', params], async () => fetchInvestorInvestorAnnualSettlements(unref(params)), {
        initialData: [] as InvestorAnnualSettlement[],
    });
};
/**
 * MonthlyInvestorShare
 */
export const useMonthlyInvestorShares = (queryOrRef?: MaybeRef<IMonthlyInvestorShareFilterOptions>, useInitial = true) => {
    return useQuery(
        ['monthly-investor-shares', queryOrRef],
        async () => getMonthlyInvestorShare(unref(queryOrRef)),
        useInitial
            ? {
                  initialData: {
                      investorShares: [],
                      pagination: {
                          size: 0,
                          page: 0,
                          total: 0,
                          hasNextPage: false,
                          hasPreviousPage: false,
                          totalPages: 0,
                      },
                  },
              }
            : {},
    );
};

export const useUpdateMonthlyInvestorShare = () => {
    const queryClient = useQueryClient();
    return useMutation(
        async (data: {
            monthlyInvestorShareId: number;
            monthlyInvestorShare?: UpdateMonthlyInvestorShareMutationRequest['data'];
            operationRows?: UpdateMonthlyInvestorShareMutationRequest['operationRows'];
        }) => {
            return updateMonthlyInvestorShare(data.monthlyInvestorShareId, data.monthlyInvestorShare, data.operationRows);
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['monthly-investor-shares'] });
            },
        },
    );
};

export const useMonthlyInvestorShareIds = (params: MaybeRef<FindInvestorShareIdsQueryParams>) => {
    return useQuery(['monthly-investor-shares-ids', params], async () => getMonthlyInvestorShareIds(unref(params)));
};

export const useMonthlyInvestorShareById = (monthlyInvestorShareId: MaybeRef<number | undefined>, opts?: GetMonthlyInvestorShareByIdQueryParams) => {
    const enabled = computed(() => !!unref(monthlyInvestorShareId));
    return useQuery(['monthly-investor-shares', 'id', monthlyInvestorShareId], async () => getMonthlyInvestorShareById(unref(monthlyInvestorShareId)!, opts), {
        enabled,
    });
};

export const useMonthlyInvestorShareStats = (params: MaybeRef<GetMonthlyInvestorShareStatsQueryParams>) => {
    const enabled = computed(() => !!unref(params));
    return useQuery(['monthly-investor-shares', 'stats', params], async () => getMonthlyInvestorShareStats(unref(params)), {
        enabled,
    });
};

export const useGenerateInvestorPayoutStatement = () =>
    useMutation(async (params: { monthlyInvestorShareId: MaybeRef<number>; data?: GenerateInvestorPayoutStatementMutationRequest }) =>
        generateInvestorPayoutStatement(unref(params.monthlyInvestorShareId), params.data),
    );

/**
 * InvestorYearlyPayout
 */
export const useInvestorYearlyPayouts = (params: MaybeRef<FindInvestorYearlyPayoutsQueryParams>) => {
    return useQuery(['investor-yearly-payouts', params], async () => fetchInvestorYearlyPayouts(unref(params)), {
        initialData: [],
    });
};

export const useInvestorYearlyPayout = (investorYearlyPayoutId: MaybeRef<number>, opts?: GetInvestorYearlyPayoutQueryParams) => {
    return useQuery(['investor-yearly-payouts', investorYearlyPayoutId], async () => getInvestorYearlyPayout(unref(investorYearlyPayoutId), opts), {
        enabled: !!unref(investorYearlyPayoutId),
    });
};

export const useSendEmailInvestorYearlyPayout = () => {
    const queryClient = useQueryClient();
    return useMutation(async (investorYearlyPayoutId: MaybeRef<number>) => sendEmailInvestorYearlyPayout(unref(investorYearlyPayoutId)), {
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['investor-yearly-payouts'] });
        },
    });
};

export const useUpdateInvestorYearlyPayout = () => {
    const queryClient = useQueryClient();
    return useMutation<IBaseInvestorYearlyPayout, unknown, MaybeRef<UpdateInvestorYearlyPayoutMutationRequest['investorYearlyPayout']>>(
        async (params: MaybeRef<UpdateInvestorYearlyPayoutMutationRequest['investorYearlyPayout']>) => updateInvestorYearlyPayout(unref(params)),
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['investor-yearly-payouts'] });
            },
        },
    );
};

export const useDeleteInvestorYearlyPayout = (onSuccess?: () => void) => {
    const queryClient = useQueryClient();
    return useMutation<void, AxiosError, MaybeRef<number>>(
        (investorYearlyPayoutId: MaybeRef<number>) => deleteInvestorYearlyPayout(unref(investorYearlyPayoutId)),
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['investor-yearly-payouts'] });
                onSuccess?.();
            },
        },
    );
};

export const useGenerateInvestorYearlyPayout = () => {
    const queryClient = useQueryClient();
    return useMutation<IBaseInvestorYearlyPayout, unknown, MaybeRef<GenerateInvestorYearlyPayoutMutationRequest>>(
        ['generate-investor-yearly-payout'],
        async (params: MaybeRef<GenerateInvestorYearlyPayoutMutationRequest>) => generateInvestorYearlyPayout(unref(params)),
        {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['investor-yearly-payouts'] });
            },
        },
    );
};
